import { useQueryClient } from "@tanstack/react-query";
import "./modalproductdetail.css";
import { useFavoriteStore } from "@/_zustand/favorite";
import { useCompanyFlow } from "@/hooks/useCompanyFlow";
import { useMachineNavigation } from "@/hooks/useMachineNavigation";
import toast from "react-hot-toast";
import Logo from "@/assets/imgs/logo.png";

/**
 * MachineCard
 *
 * Vive a nivel de módulo (no dentro de ModalProductDetail) para evitar
 * recrearse en cada render del padre y para recibir props explícitos en
 * vez de depender de un objeto ambiguo tipo `machine.machine`.
 */
function MachineCard({ machine, empresa, isFavorite, onToggleFavorite, onSelect }) {
  const handleRowActivate = () => {
    onSelect(machine.maquina_id);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleRowActivate();
    } else if (event.key === " ") {
      event.preventDefault();
      handleRowActivate();
    }
  };

  const handleFavoriteClick = (event) => {
    event.stopPropagation();
    onToggleFavorite(machine);
  };

  const logoEmpresa = machine.logo_empresa || empresa?.logo_empresa;

  return (
    <div
      className="store-card"
      role="button"
      tabIndex={0}
      aria-label={machine.maquina_nombre}
      onClick={handleRowActivate}
      onKeyDown={handleKeyDown}
    >
      {logoEmpresa && (
        <img
          src={logoEmpresa}
          alt=""
          className="store-card-empresa-logo"
        />
      )}
      <div className="store-main">
        <div className="store-main-div">
          <div className="store-main-name">
            <h4>{machine.maquina_nombre}</h4>

            <button
              type="button"
              className={`fav-heart-button ${isFavorite ? "fav-heart-fill" : "fav-heart"}`}
              onClick={handleFavoriteClick}
              aria-label={
                isFavorite
                  ? `Quitar ${machine.maquina_nombre} de favoritas`
                  : `Marcar ${machine.maquina_nombre} como favorita`
              }
              aria-pressed={isFavorite}
            />
          </div>
          <div className="tags">
            {/* {store.isOffer && (
              <span className="tag green">Ofertas del día</span>
            )}
            {store.isTopSeller && (
              <span className="tag yellow">Más vendidos</span>
            )} */}
          </div>
          <div className="location-product">
            <i className="icon-location"></i>
            <span className="text-location">
              {machine.maquina_direccion + ", " + machine.region_nombre}
            </span>
          </div>
        </div>
      </div>
      <div className="">
        <i className="icon-store-arrow"></i>
      </div>
    </div>
  );
}

export const ModalProductDetail = ({
  show,
  setShow,
  item,
  store,
  machines,
  isLoading = false,
}) => {
  const { addFavorite, removeFavorite, favorites } = useFavoriteStore();
  const { isCompanyFlow } = useCompanyFlow();
  const { goToMachine } = useMachineNavigation();
  const queryClient = useQueryClient();

  const machineList = Array.isArray(machines) ? machines : [];
  const producto = item?.current ?? null;
  const detalle = Array.isArray(producto?.detalle) ? producto.detalle[0] : null;
  const productoView = detalle ?? producto;

  const empresasCache = queryClient.getQueryData(["empresas"]);
  const empresas = Array.isArray(empresasCache) ? empresasCache : [];
  // Comparación intencional con "==": empresa_id llega como number desde
  // algunos endpoints y como string desde otros; no cambiar a "===".
  const empresaById = (id) => empresas.find((e) => e.empresa_id == id) ?? null;

  const empresaContextoId = store?.current?.empresa_id ?? null;
  // Comparación intencional con "==": empresa_id llega como number desde
  // algunos endpoints y como string desde otros; no cambiar a "===".
  const machinesVisibles =
    isCompanyFlow && empresaContextoId != null
      ? machineList.filter((m) => m.empresa_id == empresaContextoId)
      : machineList;

  const handleClose = () => {
    setShow(false);
  };

  const onSelect = async (maquinaId) => {
    setShow(false);
    await goToMachine(maquinaId);
  };

  const handleToggleFavorite = (machine) => {
    const isFav = favorites.some((fav) => fav.maquina_id === machine.maquina_id);

    if (isFav) {
      toast.success(`Se elimina como favorita la tienda ${machine.maquina_nombre}`);
      removeFavorite(machine.maquina_id);
    } else {
      const empresa = empresaById(machine.empresa_id);
      const machineToSave = {
        ...machine,
        logo_empresa: empresa?.logo_empresa ?? machine.logo_empresa,
        empresa_nombre: empresa?.empresa_nombre ?? machine.empresa_nombre,
      };
      toast.success(`Se marca como favorita la tienda ${machine.maquina_nombre}`);
      addFavorite(machineToSave);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <button className="close-button" onClick={handleClose}>
          &times;
        </button>

        <h2 className="modal-title">
          Tiendas donde puedes encontrar este producto
        </h2>

        <div className="product-summary">
          {productoView ? (
            <>
              <img
                src={productoView.producto_foto}
                alt={productoView.producto_nombre}
                className="summary-image"
              />
              <div className="summary-info">
                <a href="#" className="product-name-link">
                  {productoView.producto_nombre}
                </a>
                <div className="product-price">
                  {" "}
                  $ {productoView.producto_precio} CLP{" "}
                </div>
                <div className="original-price">
                  {" "}
                  $ {productoView.producto_precio} CLP{" "}
                </div>
              </div>
            </>
          ) : (
            <p>Sin datos del producto</p>
          )}
        </div>
        <div className="store-info">
          {isCompanyFlow && store?.current && (
            <div className="store-header">
              <img
                src={store.current.logo_empresa || Logo}
                alt="brand"
                className="brand-logo"
              />
              <h2>{store.current.empresa_nombre || "Tienda"}</h2>
            </div>
          )}

          {isLoading ? (
            <p>Buscando tiendas...</p>
          ) : machinesVisibles.length > 0 ? (
            machinesVisibles.map((m) => (
              <MachineCard
                key={`${m.maquina_id}-${m.empresa_id}`}
                machine={m}
                empresa={empresaById(m.empresa_id)}
                isFavorite={favorites.some((fav) => fav.maquina_id === m.maquina_id)}
                onToggleFavorite={handleToggleFavorite}
                onSelect={onSelect}
              />
            ))
          ) : machineList.length > 0 ? (
            <p>Este producto no está disponible en las tiendas de esta empresa.</p>
          ) : machines != null && !Array.isArray(machines) ? (
            <p>No pudimos cargar las tiendas. Intenta nuevamente.</p>
          ) : (
            <p>No hay tiendas disponibles para este producto</p>
          )}
        </div>
      </div>
    </div>
  );
};
